<template>
  <div class="sns-team-section">
    <sns-favorite-section
      class="sns-team-section__selected"
      viewType="team"
      :headingLabel="$tc('HomePage.teamsToDisplay')"
      @click="selectSnsPostDisplayTeam"
    >
      <team-card-parts
        :name="snsPostDisplayTeam ? getTeamName(snsPostDisplayTeam)[$i18n.locale] : ''"
        :img="teamLogoUrl(snsPostDisplayTeam)"
      />
    </sns-favorite-section>

    <!-- TODO: SFGO-xxx ホーム画面にシーンコメント追加 実装時にマスクを解除する -->
    <highlights-comment-trigger-parts
      v-if="false"
      :highlightTrigger="highlightTrigger"
      @click="changeHighlightsFilter"
    />

    <div
      v-if="snsContentsData.length"
      class="sns-team-section__posts"
      :class="{ 'sns-team-section__posts__isApp': isUseDynamicScroller }"
    >
      <template v-if="!isUseDynamicScroller">
        <div v-if="snsPostDisplayTeam">
          <sns-post-item-parts
            class="sns-team-section__posts__item"
            v-for="post in snsContentsData"
            :key="`${post.link}`"
            :snsPost="post"
          />
        </div>
      </template>
      <template v-else>
        <dynamic-scroller
          v-if="snsPostDisplayTeam"
          :items="snsContentsData"
          :min-item-size="68"
          key-field="link"
        >
          <template v-slot="{ item, index, active }">
            <dynamic-scroller-item
              :item="item"
              :active="active"
              :data-index="index"
              :sizeDependencies="[item.post, item.images, item.highlightComment]"
              :watchData="true"
            >
              <sns-post-item-parts
                class="sns-team-section__posts__isApp__item"
                :class="{ 'sns-team-section__posts__isApp__item--first': index === 0 }"
                :snsPost="item"
                @updateSnsContentsData="updateSnsContentsData"
              />
            </dynamic-scroller-item>
          </template>
        </dynamic-scroller>
      </template>
    </div>

    <p
      class="sns-team-section__unselected-text"
      v-else
      v-html="$tc('HomePage.favoriteTeamUnselected')"
    />

    <sns-post-loader-parts v-if="snsLoading" />
  </div>
</template>

<script lang="ts">
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { defineComponent, onMounted, ref, Ref, watch } from '@vue/composition-api'
import SnsFavoriteSection from '@/components/HomePage/HomePane/SnsFavoriteSection.vue'
import TeamCardParts from '@/components/common/card/TeamCardParts.vue'
import SnsPostLoaderParts from '@/components/HomePage/HomePane/parts/SnsPostLoaderParts.vue'
import StoreUtil from '@/store/StoreUtil'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import TeamDocument from '@/store/stores/collectionModule/documents/team/TeamDocument'
import SnsPostItemParts from '@/components/HomePage/HomePane/parts/SnsPostItemParts.vue'
import HighlightsCommentTriggerParts from '@/components/HomePage/HomePane/parts/HighlightsCommentTriggerParts.vue'
import { SnsPostData } from '@/store/stores/pageStore/HomePage/HomePageStore'
import DeviceInfo from '@/util/DeviceInfo'

/**
 * HOME SNS Teamコンテンツセクション
 */
export default defineComponent({
  name: 'SnsTeamSection',
  components: {
    HighlightsCommentTriggerParts,
    SnsFavoriteSection,
    TeamCardParts,
    SnsPostItemParts,
    SnsPostLoaderParts,
  },
  setup() {
    const store = StoreUtil.useStore('HomePageStore')
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const errorImgSrc = ref('')
    /**
     * アプリ版のAndroidのみvue-virtual-scroller（DynamicScroller）を導入する
     */
    const isUseDynamicScroller = DeviceInfo.isAndroid()

    onMounted(() => {
      // 選択しているチームのSNS投稿を取得する
      if (store.snsPostDisplayTeam.value?.teamId) {
        store.fetchHomePageSnsFeed(store.snsPostDisplayTeam.value.teamId)
      } else {
        if (!mypagePageStore.favoriteTeam.value?.teamId) {
          return
        }
        /** ログイン直後はお気に入りチームのSNSを表示 */
        store.snsPostDisplayTeam.value = mypagePageStore.favoriteTeam.value
        store.fetchHomePageSnsFeed(mypagePageStore.favoriteTeam.value.teamId)
      }
    })

    /**
     * チームロゴ画像のURL
     * @param team チーム情報
     */
    const teamLogoUrl = (team?: TeamDocument) => {
      if (!team) {
        return ''
      }
      return CloudFrontUtil.getSignedUrl(team.teamLogo)
    }
    /**
     * チーム表示名を取得する
     * @param target チーム
     */
    const getTeamName = (target: TeamDocument) => {
      if (target.teamShortName) {
        return target.teamShortName
      }
      return target.teamName
    }

    /**
     * 選択したチームのSNS記事データ
     */
    const snsContentsData = ref([]) as Ref<SnsPostData[]>

    /**
     * SNSコンテンツローディング中フラグ
     */
    const snsLoading = ref<boolean>(false)

    /**
     * ハイライトのみ絞り込みフィルター
     */
    const highlightTrigger = ref<boolean>(false)

    /**
     * store.getSnsFeedDataListの変更を監視し、リアクティブにデータを作成する
     */
    watch(
      () => store.getSnsFeedDataList.value,
      (newData) => {
        snsContentsData.value = newData.map((obj) => ({
          ...obj,
          images: obj.images.filter((image) => image.src !== errorImgSrc.value),
        }))
      },
      {
        immediate: true,
        deep: true,
      },
    )

    /**
     * imgタグで読み込みエラーになったデータを削除して、データを再構築する
     * この処理によってdynamic-scrollerで再計算が行われる
     */
    const updateSnsContentsData = (src: string) => {
      errorImgSrc.value = src
      snsContentsData.value = snsContentsData.value.map((obj) => ({
        ...obj,
        images: obj.images.filter((image) => image.src !== src),
      }))
    }

    return {
      snsPostDisplayTeam: store.snsPostDisplayTeam,
      teamLogoUrl,
      getTeamName,
      snsContentsData,
      snsLoading,
      highlightTrigger,
      updateSnsContentsData,
      isUseDynamicScroller,
    }
  },
  methods: {
    /**
     * ハイライトコメントフィルター切替
     */
    changeHighlightsFilter() {
      // TODO: 絞り込み処理
      this.highlightTrigger = !this.highlightTrigger
    },
    selectSnsPostDisplayTeam() {
      this.$router.replace({ name: 'HomeSnsTeamSelectPage' })
    },
  },
})
</script>

<style lang="scss" scoped>
.sns-team-section {
  width: 100%;

  &__posts {
    display: flex;
    flex-flow: column nowrap;
    align-content: flex-start;
    justify-items: flex-start;
    width: calc(100% - 32px);
    margin: 0 auto;
    gap: 1px;
    background: $white;
    border-radius: min(#{convertPXtoVW(8), 8px});
    border: 1px solid $black10;
    overflow: hidden;

    &__isApp {
      max-height: $snsContentHeight;

      &__item {
        &:not(&--first) {
          border-top: 1px solid $black05;
        }
      }
    }

    &__item {
      &:not(:first-child) {
        border-top: 1px solid $black05;
      }
    }
  }

  &__unselected-text {
    padding: 7px 0;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
    color: $gray50;
  }
}
</style>
